import { useEffect, useMemo, useRef } from 'react';
import type { GetEventMembershipsQuery } from './useEventMemberships.graphql.generated';
import { useGetEventMembershipsQuery } from './useEventMemberships.graphql.generated';
import { EventType } from '@graphql/generated-base';
import { useAuth } from '../AuthProvider.context';

// Extract EventMembership type from the generated query type
export type EventMembership = DeepReadonly<NonNullable<GetEventMembershipsQuery['me']>['eventMemberships'][number]>;

export interface UseEventMembershipsResult {
  eventMemberships: ReadonlyArray<EventMembership>;
  loading: boolean;
}

export type UseEventMembershipsOptions = Readonly<{
  skip?: boolean;
}>;

/**
 * Hook to fetch user's event memberships with automatic refetch on token refresh.
 * This hook automatically fires after the user is authenticated and refetches
 * when the auth token is refreshed (following the same pattern as GetMe in AuthProvider).
 *
 * @param filterEventTypes - Array of event types to filter by (defaults to wedding and babyRegistry)
 * @param options.skip - When true, the query does not fire and loading is reported as false.
 *   Callers that only need memberships under specific conditions (e.g. when a query param
 *   is present) can opt out of the network round-trip.
 * @returns Object containing eventMemberships array and loading state
 */
export const useEventMemberships = (
  filterEventTypes: EventType[] = [EventType.wedding, EventType.babyRegistry],
  options: UseEventMembershipsOptions = {},
): UseEventMembershipsResult => {
  const { isCurrentUserSettled, isLoggedIn, tokenVersion } = useAuth();
  const { skip: callerSkip = false } = options;

  // Track previous tokenVersion to detect changes (token refresh events)
  // Each component instance has its own ref, ensuring all components independently
  // detect token refreshes. Apollo's shared cache prevents duplicate network requests.
  const prevTokenVersionRef = useRef(tokenVersion);

  const { data, loading, called, refetch } = useGetEventMembershipsQuery({
    batchMode: 'off',
    variables: { filterEventTypes },
    // Only fetch when caller hasn't opted out and after user is identified
    skip: callerSkip || !isCurrentUserSettled || !isLoggedIn,
    // Use cache-first for initial load (performance)
    fetchPolicy: 'cache-first',
    // Use cache-and-network for subsequent fetches to keep data fresh
    nextFetchPolicy: 'cache-and-network',
  });

  // Refetch when token refreshes (tokenVersion increments in AuthProvider after GetMe completes)
  // Multiple components may call this hook simultaneously:
  // - Each component's effect fires independently
  // - First refetch() hits the network (due to cache-and-network policy)
  // - Subsequent refetch() calls return cached data from Apollo (no extra network requests)
  // - All components receive updated data via Apollo's cache reactivity
  //
  // callerSkip short-circuits refetch too: Apollo's `skip` prevents the initial fetch
  // but refetch() forces execution, so we must guard it independently.
  useEffect(() => {
    if (callerSkip) return;
    if (isCurrentUserSettled && isLoggedIn && tokenVersion > 0 && tokenVersion !== prevTokenVersionRef.current) {
      prevTokenVersionRef.current = tokenVersion;
      refetch();
    }
  }, [callerSkip, tokenVersion, isCurrentUserSettled, isLoggedIn, refetch]);

  const eventMemberships = useMemo<Array<EventMembership>>(() => data?.me?.eventMemberships ?? [], [data?.me?.eventMemberships]);

  // Loading is true when:
  // 1. Auth hasn't settled yet (!isCurrentUserSettled), OR
  // 2. User is logged in but query hasn't been called yet (!called && isLoggedIn), OR
  // 3. Query is in-flight (loading)
  // When callerSkip is true the caller opted out, so we report loading=false so they
  // don't wait on a fetch that will never happen.
  const isLoading = callerSkip ? false : !isCurrentUserSettled || (isLoggedIn && !called) || loading;

  return {
    eventMemberships,
    loading: isLoading,
  };
};
